// pages/handoffs.js — Handoffs / Messages page.
//
// Owns both `data-page="handoffs"` and `data-page="messages"`
// sections. The two are different URLs but render through the same
// underlying threads list (the Messages page is hidden from primary
// nav post-v0.27.1, reachable via deep-link from Coordination).
//
// Phase J6g: page-level render + drawer machinery now both live in
// the ES module graph (handoffs.js owns the drawer functions; this
// page module owns the top-level render and lifecycle). Legacy no
// longer touches the handoffs render path.
import { state } from "../state.js";
import { handoffsState } from "../page-state.js";
import { patchHTML } from "../dom.js";
import { escapeHTML } from "../util.js";
import {
  loadHandoffThreads,
  drawHandoffsBody,
  expandHandoffThread,
  refreshHandoffsBadge,
} from "../handoffs.js";

export async function renderHandoffsPage() {
  const body = document.getElementById("handoffs-body");
  if (!body) return;
  // Back/forward into /handoffs can land here with handoffsState.expanded
  // set from a prior session but expandedRows wiped (history nav doesn't
  // re-run the openThreadDrawer fetch). Re-hydrate so the user doesn't
  // see a permanent "Empty thread" placeholder.
  if (handoffsState.expanded && !handoffsState.expandedRows.length) {
    const id = handoffsState.expanded;
    // Fire-and-forget; expandHandoffThread paints its own loading/
    // error states into #handoffs-body and re-runs drawHandoffsExpanded
    // on resolve. We still call drawHandoffsBody below so the user
    // sees the loading state immediately rather than waiting on the
    // thread fetch.
    expandHandoffThread(id);
  }
  try {
    handoffsState.threads = await loadHandoffThreads();
    handoffsState.lastFetchAt = Date.now();
  } catch (e) {
    patchHTML(
      body,
      `<div class="empty">
        <div class="empty-title">Could not load handoffs</div>
        <div class="empty-detail">${escapeHTML(e.message || "fetch failed")}</div></div>`,
    );
    return;
  }
  drawHandoffsBody();
  refreshHandoffsBadge();
}

// ---- mount ----
//
// Both `handoffs` and `messages` data-page values dispatch to
// renderHandoffsPage. Bind lifecycle on BOTH sections so navigation
// to either path triggers a fresh render.

let started = false;
let tickHandler = null;

function start() {
  if (started) return;
  started = true;
  tickHandler = () => {
    const active = state.activePage;
    if (active === "handoffs" || active === "messages") {
      renderHandoffsPage();
    }
  };
  document.addEventListener("dashboard:tick", tickHandler);
  // Draw now: waiting for the next tick left an opened thread on
  // "Loading..." for as long as the tick interval.
  tickHandler();
}

function stop() {
  if (!started) return;
  started = false;
  if (tickHandler) {
    document.removeEventListener("dashboard:tick", tickHandler);
    tickHandler = null;
  }
}

function bindLifecycle() {
  const sections = document.querySelectorAll(
    'section.page[data-page="handoffs"], section.page[data-page="messages"]',
  );
  if (!sections.length) return;

  let anyActive = false;
  sections.forEach((s) => {
    if (s.classList.contains("active")) anyActive = true;
  });
  if (anyActive) start();

  sections.forEach((section) => {
    const obs = new MutationObserver((mutations) => {
      mutations.forEach((m) => {
        if (m.attributeName !== "class") return;
        // Check the union: any handoffs/messages section active.
        let active = false;
        sections.forEach((s) => {
          if (s.classList.contains("active")) active = true;
        });
        if (active) start();
        else stop();
      });
    });
    obs.observe(section, { attributes: true });
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", bindLifecycle);
} else {
  bindLifecycle();
}
